import {
  FilterOperation,
  IFilterInput,
  IFinancialCalendarInput,
  INominalTransaction,
  INominalTransactionsQueryResult,
  INominalTransactionsQueryVariables,
  Maybe,
} from '../../graphql/schema/p4types';
import { useQuery } from '@apollo/client';
import NominalTransactionWorkspaceQuery from '../../graphql/queries/nominal/NominalTransactionWorkspaceQuery.graphql';
import { useEffect, useMemo } from 'react';
import { flattenObjectArray } from '../flattenObject';

/**
 * Retrieve the nominal transactions for a given nominal id and financial year
 *
 * @param nominalId - the nominal id we want the calendars for
 * @param financialYear - the financial year, if undefined or null an empty transaction array will be returned
 */
export function useFlatNominalTransactions(
  nominalId: number,
  financialYear: Maybe<IFinancialCalendarInput>
): { data: Array<any>; loading: boolean; progress: number } {
  const filters: Array<IFilterInput> = [
    financialYear
      ? {
          filterOperation: FilterOperation.Equals,
          filterParameters: {
            field: 'financialPeriod.financialCalendar.id',
            value: financialYear.id,
          },
        }
      : null,
    nominalId ?? -1 > 0
      ? {
          filterOperation: FilterOperation.Equals,
          filterParameters: {
            field: 'nominal.id',
            value: nominalId,
          },
        }
      : null,
  ].filter((value) => !!value) as Array<IFilterInput>;

  const { data, fetchMore, loading } = useQuery<INominalTransactionsQueryResult, INominalTransactionsQueryVariables>(
    NominalTransactionWorkspaceQuery,
    { variables: { filters: filters } }
  );

  //This effect will run fetchMore calls until we have all transactions have been fetched.
  useEffect(() => {
    if (!loading && data?.listData?.pageInfo.hasNextPage) {
      fetchMore({
        variables: {
          after: data?.listData?.pageInfo.endCursor,
        },
      });
    }
  }, [data?.listData?.pageInfo.endCursor, data?.listData?.pageInfo.hasNextPage, fetchMore, loading]);

  return useMemo(() => {
    const isLoading = loading || (data?.listData?.pageInfo.hasNextPage ?? false);
    const currentCount = parseInt(data?.listData?.pageInfo.endCursor ?? '0', 10);
    const finishedCount = (data?.listData?.count ?? 0) > 0 ? data?.listData?.count : 1; //Default to 1 since we divide by this (I.e. Don't use 0!).

    if (!data?.listData?.edges || isLoading) {
      return { data: [], loading: isLoading, progress: currentCount / finishedCount };
    }
    const flatData = flattenObjectArray(data.listData.edges.map((edge) => edge!.node as INominalTransaction));
    return {
      data: flatData,
      loading: isLoading,
      progress: 1,
    };
  }, [
    data?.listData?.edges,
    data?.listData?.count,
    data?.listData?.pageInfo.endCursor,
    data?.listData?.pageInfo.hasNextPage,
    loading,
  ]);
}
